import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import SettingsInputAntennaIcon from "@material-ui/icons/SettingsInputAntenna";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import { FormDialogHeader, useFormDialogStyles } from "../../components/FormDialog";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import listPageStyles from "../../styles/listPageStyles";
import { buildSample, parseNumber, VARIABLES } from "./codeSamples";
import DocsPanel from "./DocsPanel";

const TONES = {
  green: { light: "#2e7d32", dark: "#81c784" },
  red: { light: "#c62828", dark: "#e57373" },
  amber: { light: "#b26a00", dark: "#ffd54f" },
};
const tone = (theme, name) => TONES[name][theme.palette.type === "dark" ? "dark" : "light"];

const useStyles = makeStyles((theme) => {
  const pill = (name) => ({
    color: tone(theme, name),
    backgroundColor: alpha(tone(theme, name), theme.palette.type === "dark" ? 0.18 : 0.12),
  });
  const codeBg = theme.palette.type === "dark" ? "#161c2e" : "#f3f2ee";
  return {
    ...listPageStyles(theme),
    root: {
      color: theme.palette.text.primary,
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.75),
      paddingBottom: theme.spacing(3),
    },
    muted: { color: theme.palette.text.secondary },
    tabs: { display: "flex", gap: 4, padding: theme.spacing(0, 0.75) },
    tab: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: 13,
      borderRadius: 999,
      padding: theme.spacing(0.75, 1.75),
      minHeight: 0,
      color: theme.palette.text.secondary,
      border: "1px solid transparent",
    },
    tabActive: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
    },
    card: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 14,
      minWidth: 0,
    },
    cardHeader: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      padding: theme.spacing(1.75, 2.25),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    cardTitle: { fontSize: 15, fontWeight: 600 },
    cardBody: { padding: theme.spacing(2, 2.25) },
    conns: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
      gap: theme.spacing(1.25),
    },
    conn: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
      textAlign: "left",
      padding: theme.spacing(1.5, 1.75),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
    },
    connSelected: {
      borderColor: theme.palette.primary.main,
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    connTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: theme.spacing(1) },
    connName: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    connNumber: { fontSize: 12.5, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
    tokenRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: theme.spacing(0.75), fontSize: 12.5 },
    tokenMask: {
      fontFamily: "monospace",
      fontSize: 12,
      backgroundColor: codeBg,
      borderRadius: 6,
      padding: "2px 7px",
    },
    linkButton: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: 12.5,
      padding: "2px 6px",
      minWidth: 0,
    },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      height: 22,
      padding: "0 9px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    pill_green: pill("green"),
    pill_red: pill("red"),
    pill_amber: pill("amber"),
    grid2: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
      gap: theme.spacing(1.75),
      alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
    row2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(1.5),
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    field: { "& .MuiOutlinedInput-root": { borderRadius: 10 } },
    chips: { display: "flex", gap: 6, flexWrap: "wrap" },
    chip: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: 12.5,
      height: 30,
      borderRadius: 999,
      padding: theme.spacing(0, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
    chipOn: {
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.35),
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    vars: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, fontSize: 12, color: theme.palette.text.secondary },
    varButton: {
      textTransform: "none",
      fontFamily: "monospace",
      fontSize: 12,
      padding: "1px 7px",
      minWidth: 0,
      borderRadius: 6,
      border: `1px dashed ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
    files: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(1),
      border: `1.5px dashed ${theme.palette.divider}`,
      borderRadius: 12,
      padding: theme.spacing(1.5),
    },
    fileItem: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      maxWidth: "100%",
      backgroundColor: codeBg,
      borderRadius: 8,
      padding: theme.spacing(0.5, 0.5, 0.5, 1.25),
      fontSize: 13,
      "& span": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    },
    hiddenInput: { display: "none" },
    actions: { display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: theme.spacing(1) },
    primaryButton: { textTransform: "none", fontWeight: 600, borderRadius: 10, height: 40, padding: theme.spacing(0, 2) },
    result: {
      display: "flex",
      gap: theme.spacing(1.25),
      alignItems: "flex-start",
      borderRadius: 10,
      padding: theme.spacing(1.25, 1.5),
      fontSize: 13,
      "& b": { display: "block" },
    },
    resultOk: { ...pill("green"), color: theme.palette.text.primary, "& svg": { color: tone(theme, "green") } },
    resultErr: { ...pill("red"), color: theme.palette.text.primary, "& svg": { color: tone(theme, "red") } },
    endpoint: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
      "& code": { fontSize: 12.5, wordBreak: "break-all" },
    },
    method: {
      fontFamily: "monospace",
      fontWeight: 700,
      fontSize: 11,
      color: "#fff",
      backgroundColor: tone(theme, "green"),
      borderRadius: 6,
      padding: "3px 7px",
    },
    code: {
      margin: 0,
      backgroundColor: codeBg,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: theme.spacing(1.75),
      fontFamily: "monospace",
      fontSize: 12.3,
      lineHeight: 1.55,
      overflowX: "auto",
      whiteSpace: "pre",
    },
    codeFooter: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.25),
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    empty: { padding: theme.spacing(2), textAlign: "center", color: theme.palette.text.secondary },
    // documentação
    docs: { display: "flex", flexDirection: "column", gap: theme.spacing(2.5), maxWidth: 860, padding: theme.spacing(2.5) },
    docsTitle: { fontSize: 14, fontWeight: 600, marginBottom: theme.spacing(1) },
    docsText: { maxWidth: "68ch", marginBottom: theme.spacing(1) },
    tableScroll: { overflowX: "auto" },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 13,
      "& th": {
        textAlign: "left",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        padding: theme.spacing(1, 1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      "& td": { padding: theme.spacing(1.1, 1.25), borderBottom: `1px solid ${theme.palette.divider}`, verticalAlign: "top" },
      "& tr:last-child td": { borderBottom: "none" },
      "& td code": { whiteSpace: "nowrap" },
    },
    note: {
      fontSize: 13,
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 10,
      ...pill("amber"),
      color: theme.palette.text.primary,
    },
    tokenBox: {
      fontFamily: "monospace",
      fontSize: 14,
      wordBreak: "break-all",
      backgroundColor: codeBg,
      borderRadius: 10,
      padding: theme.spacing(1.5),
      margin: theme.spacing(1.5, 0),
    },
  };
});

const STATUS = {
  CONNECTED: { label: "Conectada", tone: "green" },
  qrcode: { label: "Aguardando QR Code", tone: "amber" },
  OPENING: { label: "Conectando", tone: "amber" },
  PAIRING: { label: "Conectando", tone: "amber" },
  TIMEOUT: { label: "Instável", tone: "amber" },
};
const statusOf = (status) => STATUS[status] || { label: "Desconectada", tone: "red" };

const formatPhone = (number) => {
  const d = String(number || "").replace(/\D/g, "");
  if (d.length < 12) return d || "Sem número";
  return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, -4)}-${d.slice(-4)}`;
};

const errorText = (err) => {
  const msg = err?.response?.data?.error;
  if (msg) return i18n.exists(`backendErrors.${msg}`) ? i18n.t(`backendErrors.${msg}`) : msg;
  return "Não foi possível enviar agora. Tente novamente.";
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copiado");
  } catch (e) {
    toast.error("Não foi possível copiar. Selecione o texto e copie manualmente.");
  }
};

const MessagesAPI = () => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const bodyRef = useRef(null);

  const [tab, setTab] = useState("test");
  const [connections, setConnections] = useState([]);
  const [loadingConns, setLoadingConns] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  // Token completo só existe aqui logo depois de gerado (o servidor não devolve depois).
  const [revealed, setRevealed] = useState({});
  const [confirmTokenFor, setConfirmTokenFor] = useState(null);
  const [newToken, setNewToken] = useState(null);

  const [type, setType] = useState("text");
  const [lang, setLang] = useState("curl");
  const [number, setNumber] = useState("");
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [closeTicket, setCloseTicket] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const endpoint = `${String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/messages/send`;
  const isAdmin = !user?.profile || user.profile === "admin";

  const loadConnections = useCallback(async () => {
    setLoadingConns(true);
    try {
      const { data } = await api.get("/messages-api/connections");
      setConnections(data);
      setSelectedId((current) => {
        if (current && data.some((c) => c.id === current)) return current;
        const best =
          data.find((c) => c.hasToken && c.status === "CONNECTED") ||
          data.find((c) => c.status === "CONNECTED") ||
          data[0];
        return best ? best.id : null;
      });
    } catch (err) {
      setConnections([]);
      toast.error(errorText(err));
    }
    setLoadingConns(false);
  }, []);

  useEffect(() => {
    if (isAdmin) loadConnections();
  }, [isAdmin, loadConnections]);

  const selected = connections.find((c) => c.id === selectedId) || null;
  const parsed = parseNumber(number);

  const sample = useMemo(
    () =>
      buildSample({
        lang,
        endpoint,
        token: (selected && revealed[selected.id]) || "SEU_TOKEN",
        type,
        number: parsed.digits,
        body,
        name,
        closeTicket,
        fileName: files[0]?.name,
      }),
    [lang, endpoint, selected, revealed, type, parsed.digits, body, name, closeTicket, files]
  );

  if (!isAdmin) return <Redirect to="/tickets" />;

  const generateToken = async (connection) => {
    try {
      const { data } = await api.post(`/messages-api/connections/${connection.id}/token`);
      setRevealed((prev) => ({ ...prev, [connection.id]: data.token }));
      setConnections((prev) =>
        prev.map((c) => (c.id === connection.id ? { ...c, hasToken: true, tokenLast4: data.tokenLast4 } : c))
      );
      setSelectedId(connection.id);
      setNewToken({ name: connection.name, token: data.token });
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const insertVariable = (key) => {
    const el = bodyRef.current;
    const start = el ? el.selectionStart : body.length;
    const end = el ? el.selectionEnd : body.length;
    const next = body.slice(0, start) + key + body.slice(end);
    setBody(next);
    setTimeout(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + key.length, start + key.length);
    }, 0);
  };

  const fail = (title, text) => setResult({ ok: false, title, text });

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selected) return fail("Escolha uma conexão", "Selecione acima o número que vai enviar a mensagem.");
    if (selected.status !== "CONNECTED") {
      return fail(`A conexão ${selected.name} está desconectada`, "Leia o QR Code em Conexões para voltar a enviar.");
    }
    if (!parsed.valid) return fail("Número incompleto", "Use 55 + DDD + número, por exemplo 55 11 98765-4321.");
    if (type === "text" && !body.trim()) return fail("Escreva a mensagem", "O campo Mensagem está vazio.");
    if (type === "media" && files.length === 0) return fail("Escolha um arquivo", "Selecione pelo menos um arquivo para enviar.");

    const data = new FormData();
    data.append("whatsappId", selected.id);
    data.append("number", parsed.digits);
    if (body) data.append("body", body);
    if (name.trim()) data.append("name", name.trim());
    if (closeTicket) data.append("closeTicket", "true");
    if (type === "media") files.forEach((f) => data.append("medias", f));

    setSending(true);
    setResult(null);
    try {
      await api.post("/messages-api/test", data);
      setResult({
        ok: true,
        title: `Mensagem enviada para ${parsed.digits}`,
        text: `O atendimento foi aberto em Atendimentos, pela conexão ${selected.name}.`,
      });
      setBody("");
      setFiles([]);
    } catch (err) {
      // Mantém o que foi digitado para a pessoa corrigir e tentar de novo.
      fail("A mensagem não foi enviada", errorText(err));
    }
    setSending(false);
  };

  const renderConnection = (c) => {
    const st = statusOf(c.status);
    return (
      <ButtonBase
        key={c.id}
        component="div"
        className={`${classes.conn} ${c.id === selectedId ? classes.connSelected : ""}`}
        onClick={() => setSelectedId(c.id)}
        role="radio"
        aria-checked={c.id === selectedId}
      >
        <div className={classes.connTop}>
          <span className={classes.connName}>{c.name}</span>
          <span className={`${classes.pill} ${classes[`pill_${st.tone}`]}`}>{st.label}</span>
        </div>
        <div className={classes.connNumber}>{formatPhone(c.number)}</div>
        <div className={classes.tokenRow}>
          {c.hasToken ? (
            <>
              <span className={classes.tokenMask}>••••••••{c.tokenLast4}</span>
              {revealed[c.id] && (
                <Button
                  size="small"
                  color="primary"
                  className={classes.linkButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyText(revealed[c.id]);
                  }}
                >
                  Copiar token
                </Button>
              )}
              <Button
                size="small"
                color="primary"
                className={classes.linkButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmTokenFor(c);
                }}
              >
                Trocar token
              </Button>
            </>
          ) : (
            <>
              <span className={`${classes.pill} ${classes.pill_amber}`}>Sem token</span>
              <Button
                size="small"
                color="primary"
                className={classes.linkButton}
                onClick={(e) => {
                  e.stopPropagation();
                  generateToken(c);
                }}
              >
                Gerar token
              </Button>
            </>
          )}
        </div>
      </ButtonBase>
    );
  };

  const tokenHint = !selected
    ? "Escolha uma conexão para montar o exemplo."
    : revealed[selected.id]
    ? `Usando o token gerado agora para ${selected.name}.`
    : selected.hasToken
    ? `Troque SEU_TOKEN pelo token de ${selected.name} (termina em ${selected.tokenLast4}).`
    : `${selected.name} ainda não tem token: clique em Gerar token.`;

  return (
    <MainContainer>
      <ConfirmationModal
        title={`Trocar o token de ${confirmTokenFor?.name || ""}?`}
        open={Boolean(confirmTokenFor)}
        onClose={() => setConfirmTokenFor(null)}
        onConfirm={() => generateToken(confirmTokenFor)}
      >
        O token atual para de funcionar na hora. Integrações que usam ele vão receber erro 401 até
        você colocar o novo token nelas.
      </ConfirmationModal>

      <Dialog
        open={Boolean(newToken)}
        onClose={() => setNewToken(null)}
        classes={{ paper: dialogClasses.paper }}
        maxWidth="xs"
        fullWidth
      >
        <FormDialogHeader
          title="Token gerado"
          subtitle={newToken ? `Conexão ${newToken.name}` : ""}
          onClose={() => setNewToken(null)}
        />
        <DialogContent dividers className={dialogClasses.content}>
          <Typography variant="body2">
            Copie e guarde agora. Por segurança, depois que você sair desta tela o sistema mostra só
            os 4 últimos caracteres.
          </Typography>
          <div className={classes.tokenBox}>{newToken?.token}</div>
        </DialogContent>
        <DialogActions className={dialogClasses.actions}>
          <Button onClick={() => setNewToken(null)} className={classes.primaryButton}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.primaryButton}
            startIcon={<FileCopyOutlinedIcon />}
            onClick={() => copyText(newToken.token)}
          >
            Copiar token
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.root}>
        <div className={classes.header} style={{ paddingBottom: 0 }}>
          <div className={classes.headerTitle}>
            <Title>API de mensagens</Title>
            <Typography variant="body2" className={classes.muted}>
              Envie mensagens do WhatsApp a partir do seu CRM, ERP ou automação (n8n, Make, Zapier)
            </Typography>
          </div>
          <div className={classes.headerButtons}>
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton}`}
              startIcon={<FileCopyOutlinedIcon />}
              onClick={() => copyText(endpoint)}
            >
              Copiar endpoint
            </Button>
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton}`}
              startIcon={<SettingsInputAntennaIcon />}
              onClick={() => history.push("/connections")}
            >
              Abrir Conexões
            </Button>
          </div>
        </div>

        <div className={classes.tabs} role="tablist">
          {[
            ["test", "Testar envio"],
            ["docs", "Documentação"],
          ].map(([key, label]) => (
            <Button
              key={key}
              role="tab"
              aria-selected={tab === key}
              className={`${classes.tab} ${tab === key ? classes.tabActive : ""}`}
              onClick={() => setTab(key)}
            >
              {label}
            </Button>
          ))}
        </div>

        {tab === "docs" ? (
          <div className={classes.card}>
            <DocsPanel classes={classes} endpoint={endpoint} />
          </div>
        ) : (
          <>
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <Typography className={classes.cardTitle}>1. Escolha a conexão que vai enviar</Typography>
                <Typography variant="body2" className={classes.muted}>
                  Cada conexão tem o próprio token
                </Typography>
              </div>
              <div className={classes.cardBody}>
                {loadingConns ? (
                  <div className={classes.empty}>
                    <CircularProgress size={22} />
                  </div>
                ) : connections.length === 0 ? (
                  <div className={classes.empty}>
                    Nenhuma conexão cadastrada. Crie uma em Conexões para usar a API.
                  </div>
                ) : (
                  <div className={classes.conns} role="radiogroup">
                    {connections.map(renderConnection)}
                  </div>
                )}
              </div>
            </div>

            <div className={classes.grid2}>
              <div className={classes.card}>
                <div className={classes.cardHeader}>
                  <Typography className={classes.cardTitle}>2. Monte a mensagem</Typography>
                  <div className={classes.chips}>
                    {[
                      ["text", "Texto"],
                      ["media", "Arquivo"],
                    ].map(([key, label]) => (
                      <Button
                        key={key}
                        className={`${classes.chip} ${type === key ? classes.chipOn : ""}`}
                        onClick={() => setType(key)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
                <form className={`${classes.cardBody} ${classes.form}`} onSubmit={handleSend} noValidate>
                  <div className={classes.row2}>
                    <TextField
                      id="api-number"
                      label="Número do WhatsApp"
                      variant="outlined"
                      size="small"
                      className={classes.field}
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      inputProps={{ inputMode: "tel" }}
                      error={Boolean(parsed.digits) && !parsed.valid}
                      helperText={
                        !parsed.digits
                          ? "Ex.: 55 11 98765-4321"
                          : parsed.valid
                          ? `Será enviado como ${parsed.digits}`
                          : "Inclua 55 + DDD + número"
                      }
                    />
                    <TextField
                      id="api-name"
                      label="Nome do contato (opcional)"
                      variant="outlined"
                      size="small"
                      className={classes.field}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      helperText="Usado só se o contato ainda não existir"
                    />
                  </div>

                  {type === "media" && (
                    <div className={classes.files}>
                      {files.map((f, i) => (
                        <div key={`${f.name}-${i}`} className={classes.fileItem}>
                          <span>{f.name}</span>
                          <Button
                            size="small"
                            aria-label={`Remover ${f.name}`}
                            style={{ minWidth: 0, padding: 4 }}
                            onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          >
                            <CloseIcon fontSize="small" />
                          </Button>
                        </div>
                      ))}
                      <input
                        id="api-file-upload"
                        type="file"
                        multiple
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                        className={classes.hiddenInput}
                        onChange={(e) => {
                          const picked = Array.from(e.target.files || []);
                          setFiles((prev) => [...prev, ...picked]);
                          e.target.value = "";
                        }}
                      />
                      <label htmlFor="api-file-upload">
                        <Button component="span" variant="outlined" color="primary" size="small" startIcon={<AttachFileIcon />}>
                          {files.length ? "Adicionar arquivo" : "Selecionar arquivo"}
                        </Button>
                      </label>
                    </div>
                  )}

                  <div>
                    <TextField
                      id="api-body"
                      label={type === "media" ? "Legenda (opcional)" : "Mensagem"}
                      variant="outlined"
                      className={classes.field}
                      fullWidth
                      multiline
                      minRows={4}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      inputRef={bodyRef}
                    />
                    <div className={classes.vars} style={{ marginTop: 8 }}>
                      Variáveis:
                      {VARIABLES.map((v) => (
                        <Button
                          key={v.key}
                          className={classes.varButton}
                          title={v.label}
                          onClick={() => insertVariable(v.key)}
                        >
                          {v.key}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={closeTicket}
                        onChange={(e) => setCloseTicket(e.target.checked)}
                      />
                    }
                    label={
                      <span>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          Encerrar o atendimento depois de enviar
                        </Typography>
                        <Typography variant="caption" className={classes.muted}>
                          Útil para avisos automáticos que não esperam resposta
                        </Typography>
                      </span>
                    }
                  />

                  <div className={classes.actions}>
                    <Button
                      className={classes.primaryButton}
                      onClick={() => {
                        setNumber("");
                        setName("");
                        setBody("");
                        setFiles([]);
                        setCloseTicket(false);
                        setResult(null);
                      }}
                    >
                      Limpar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disableElevation
                      disabled={sending}
                      className={classes.primaryButton}
                      startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    >
                      {sending ? "Enviando…" : "Enviar teste"}
                    </Button>
                  </div>

                  {result && (
                    <div className={`${classes.result} ${result.ok ? classes.resultOk : classes.resultErr}`} role="status">
                      {result.ok ? <CheckCircleOutlineIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
                      <div>
                        <b>{result.title}</b>
                        {result.text}
                      </div>
                    </div>
                  )}
                </form>
              </div>

              <div className={classes.card}>
                <div className={classes.cardHeader}>
                  <Typography className={classes.cardTitle}>3. Copie para a sua integração</Typography>
                  <div className={classes.chips}>
                    {[
                      ["curl", "cURL"],
                      ["js", "JavaScript"],
                      ["n8n", "n8n"],
                    ].map(([key, label]) => (
                      <Button
                        key={key}
                        className={`${classes.chip} ${lang === key ? classes.chipOn : ""}`}
                        onClick={() => setLang(key)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className={classes.cardBody}>
                  <div className={classes.endpoint}>
                    <span className={classes.method}>POST</span>
                    <code>{endpoint}</code>
                  </div>
                  <pre className={classes.code}>{sample}</pre>
                  <div className={classes.codeFooter}>
                    <span>{tokenHint}</span>
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.secondaryButton}
                      startIcon={<FileCopyOutlinedIcon />}
                      onClick={() => copyText(sample)}
                    >
                      Copiar código
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default MessagesAPI;
